import React from 'react';
import { Check } from 'lucide-react';
import WelcomeExampleBrief from './WelcomeExampleBrief';

// Capabilities that exist in the product today.
const PILLARS = [
  {
    num: '01',
    title: 'Threat analysis',
    desc: 'Public advisories and security research are collected, de-duplicated and matched against your industry, region and security stack.',
    points: ['Profile-aware matching', 'MITRE ATT&CK mapping', 'CVE enrichment with CISA KEV', 'Weekly PDF threat report'],
  },
  {
    num: '02',
    title: 'Breach monitoring',
    desc: 'Monitor your verified domains and mailboxes against breach sources, and get alerted when your credentials appear.',
    points: ['Credential exposure alerts', 'Domain and mailbox coverage', 'Password detail only after DNS verification', 'Acknowledgement workflow'],
  },
  {
    num: '03',
    title: 'Response',
    desc: 'Turn a brief into detection: rules are built from the indicators in the brief, ready to export or deliver by webhook.',
    points: ['YARA and Sigma rules', 'Splunk SPL, Elastic KQL, QRadar AQL', 'Webhook delivery', 'Threat hunt campaigns'],
  },
];

const FACTS = [
  { k: 'Profile-aware matching', v: 'Industry, region and security stack' },
  { k: 'MITRE ATT&CK', v: 'Techniques tagged on each brief' },
  { k: 'Rule generation', v: 'YARA · Sigma · SPL · KQL · AQL' },
  { k: 'Reporting', v: 'Weekly PDF for leadership' },
];

export default function WelcomeFeatureGrid() {
  return (
    <>
      <section id="platform" className="wl-section" aria-labelledby="wl-platform-title">
        <div className="wl-container">
          <div className="wl-head">
            <div className="wl-eyebrow">The platform</div>
            <h2 id="wl-platform-title" className="wl-h2">
              Three disciplines, <em>one correlated view.</em>
            </h2>
            <p className="wl-lede">
              Threat analysis, breach monitoring and detection engineering share one profile
              of your organization, so every team works from the same picture.
            </p>
          </div>

          <div className="wl-pillars">
            {PILLARS.map((p) => (
              <div key={p.num}>
                <div className="wl-num">{p.num}</div>
                <h3 className="wl-h3">{p.title}</h3>
                <p className="wl-body">{p.desc}</p>
                <ul className="wl-points">
                  {p.points.map((pt) => (
                    <li key={pt}>
                      <Check size={13} strokeWidth={2.2} aria-hidden="true" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="workspace" className="wl-section wl-section--band" aria-labelledby="wl-workspace-title">
        <div className="wl-container">
          <div className="wl-split">
            <div>
              <div className="wl-eyebrow">Analyst workspace</div>
              <h2 id="wl-workspace-title" className="wl-h2">
                One Inbox for the threats <em>aimed at you.</em>
              </h2>
              <p className="wl-lede">
                Each brief arrives triaged by severity, with sources merged, techniques mapped
                and detection rules attached. Work it like a queue: triage, acknowledge, ship
                the rule.
              </p>
              <dl className="wl-facts">
                {FACTS.map((r) => (
                  <div key={r.k}>
                    <dt>{r.k}</dt>
                    <dd>{r.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <WelcomeExampleBrief />
          </div>
        </div>
      </section>
    </>
  );
}
